import {
    COUNTRY_CANADA,
    COUNTRY_UNITED_STATES,
    postalFieldLabel,
    regionFieldLabel
} from './shipToRegions';
import type { ShipToFormValues } from './shipToTypes';

export type ShipToFormErrors = Partial<Record<keyof ShipToFormValues, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const US_ZIP_PATTERN = /^\d{5}(-\d{4})?$/;
const CA_POSTAL_PATTERN = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

const REQUIRED_FIELDS: Array<{ field: keyof ShipToFormValues; label: string }> = [
    { field: 'name', label: 'Name' },
    { field: 'address', label: 'Address' },
    { field: 'city', label: 'City' },
    { field: 'state', label: 'State' },
    { field: 'zip', label: 'ZIP' },
    { field: 'country', label: 'Country' }
];

function validateZipFormat(values: ShipToFormValues): string | undefined {
    const zip = values.zip.trim();
    if (!zip) return undefined;

    const isCanada = values.country === COUNTRY_CANADA;
    const pattern = isCanada ? CA_POSTAL_PATTERN : US_ZIP_PATTERN;
    if (pattern.test(zip)) return undefined;

    const label = postalFieldLabel(isCanada ? COUNTRY_CANADA : COUNTRY_UNITED_STATES).toLowerCase();
    const example = isCanada ? 'A1A 1A1' : '12345 or 12345-6789';
    return `Enter a valid ${label} (e.g. ${example}).`;
}

export function validateShipTo(values: ShipToFormValues): ShipToFormErrors {
    const errors: ShipToFormErrors = {};

    for (const { field, label } of REQUIRED_FIELDS) {
        if (!values[field].trim()) errors[field] = `${label} is required.`;
    }

    const country = values.country === COUNTRY_CANADA ? COUNTRY_CANADA : COUNTRY_UNITED_STATES;
    if (errors.state) {
        errors.state = `${regionFieldLabel(country)} is required.`;
    }
    if (errors.zip) {
        errors.zip = `${postalFieldLabel(country)} is required.`;
    }

    if (values.email.trim() && !EMAIL_PATTERN.test(values.email.trim())) {
        errors.email = 'Enter a valid email address.';
    }

    if (!errors.zip) {
        const zipError = validateZipFormat(values);
        if (zipError) errors.zip = zipError;
    }

    return errors;
}

export function isShipToValid(values: ShipToFormValues): boolean {
    return Object.keys(validateShipTo(values)).length === 0;
}
